import type React from 'react'
import { useCallback, useRef, useState } from 'react'
import { DEFAULT_POINT_SIZE } from '@/lib/text/constants/text-defaults'
import type { TextRunStyleUpdate } from '@/lib/text/types/text-run-style-update'
import { FONT_SIZE_MAX, FONT_SIZE_MIN, FONT_SIZE_PRESETS } from '../constants/typography-ranges'

interface FontSizeFieldOptions {
  disabled: boolean
  defaultFontSize: number | undefined
  emitStyleUpdate: (update: TextRunStyleUpdate) => void
  restoreActiveEditor: () => void
}

/** Font Size: preset chips, slider and numeric input, plus how a selection is shown. */
export function useFontSizeField({
  disabled,
  defaultFontSize,
  emitStyleUpdate,
  restoreActiveEditor,
}: FontSizeFieldOptions) {
  const [fontSize, setFontSize] = useState([defaultFontSize ?? DEFAULT_POINT_SIZE])
  const [fontSizeInputValue, setFontSizeInputValue] = useState<string>(
    String(defaultFontSize ?? DEFAULT_POINT_SIZE)
  )
  const [fontSizeError, setFontSizeError] = useState(false)
  const [fontSizeMixed, setFontSizeMixed] = useState(false)
  // The size the user last applied here (never a resync), a fresh object per apply. Read by
  // `useLeadingPresetFollow`.
  const [appliedFontSize, setAppliedFontSize] = useState<{ size: number } | null>(null)
  // Prevent the selection-sync effect from overwriting fontSizeInputValue while the user is
  // actively editing it (arrow keys, typing). Without this guard the effect fires after each
  // ArrowUp/Down (because selectionchange + textPanelRevision both bump), reads the OLD caret
  // style from window.getSelection(), and resets the input back to the pre-update value.
  const fontSizeInputFocused = useRef(false)

  // Unlike Alignment/Leading, Font Size is also actionable during live text-edit mode (a caret in
  // a shape being typed into, where `selectedShapeTextParagraphs` is null but `emitStyleUpdate`
  // still has a real target via `getEditingShapeTextId`) — so it must not gate on
  // `hasTextSelectionTarget`, only on the panel's own top-level `disabled`.
  const fontSizeControlsDisabled = disabled

  // A Narrow/Wide leading preset is defined relative to font size, so it must track font size
  // changes rather than freezing at the pt value computed when the chip was first clicked.
  // Consumers depend on fontSize[0] (a primitive), not the fontSize array — the sync effect
  // creates a new array on every tick even when the value is unchanged, which would otherwise
  // re-run the leading-follow effect (and re-check leadingPt) on every caret move.
  const fontSizeValue = fontSizeMixed ? DEFAULT_POINT_SIZE : fontSize[0]

  // Only highlight a preset chip when the size matches it exactly (nearest-rounding would always
  // light one up, falsely implying e.g. 100pt == 24pt).
  const activeFontSizePreset =
    !fontSizeMixed && (FONT_SIZE_PRESETS as readonly number[]).includes(fontSize[0])
      ? String(fontSize[0])
      : ''

  const commitFontSize = (size: number) => {
    setFontSizeMixed(false)
    setFontSize([size])
    setFontSizeInputValue(String(size))
    setFontSizeError(false)
    setAppliedFontSize({ size })
    emitStyleUpdate({ pointSize: size })
  }

  const handlePresetChange = (val: string) => {
    if (fontSizeControlsDisabled) return
    if (!val) return
    commitFontSize(parseInt(val))
  }

  const handleSliderChange = (val: number[]) => {
    if (fontSizeControlsDisabled) return
    commitFontSize(val[0])
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (fontSizeControlsDisabled) return

    const val = e.target.value.replace(/[^0-9]/g, '')

    setFontSizeMixed(false)
    setFontSizeInputValue(val)
    const n = parseInt(val, 10)
    const isValid = !isNaN(n) && n >= FONT_SIZE_MIN && n <= FONT_SIZE_MAX

    setFontSizeError(!isValid && val !== '')
    if (isValid) {
      setFontSize([n])
      setAppliedFontSize({ size: n })
      emitStyleUpdate({ pointSize: n })
    }
  }

  const handleInputFocus = () => {
    fontSizeInputFocused.current = true
  }

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (fontSizeControlsDisabled) return
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault()
      const current = parseInt(fontSizeInputValue, 10) || fontSize[0]
      const newVal = e.key === 'ArrowUp' ? current + 1 : current - 1
      const clamped = Math.max(FONT_SIZE_MIN, Math.min(FONT_SIZE_MAX, newVal))

      commitFontSize(clamped)
      // emitStyleUpdate → handleEditingStyleUpdate → sel.addRange() can move
      // keyboard focus to the contenteditable. Refocus this input immediately so
      // subsequent arrow presses continue incrementing instead of moving the cursor.
      ;(e.target as HTMLInputElement).focus()
    }
  }

  const handleInputBlur = () => {
    fontSizeInputFocused.current = false

    if (!fontSizeMixed) {
      const n = parseInt(fontSizeInputValue, 10)

      if (isNaN(n) || n < FONT_SIZE_MIN || n > FONT_SIZE_MAX) {
        setFontSizeInputValue(String(fontSize[0]))
        setFontSizeError(false)
      }
    }

    restoreActiveEditor()
  }

  /** Live text editing: show the editor's selection style, unless the input is being edited. */
  const syncFromLiveEdit = useCallback((style: TextRunStyleUpdate) => {
    if (fontSizeInputFocused.current) return

    if (style.mixedPointSize) {
      // A selection spanning more than one point size (e.g. part of it was just stepped via
      // Up/Down while the rest wasn't) shows the same blank "Mixed" placeholder the
      // Select-Tool whole-shape path already uses, instead of one run's size read as if it
      // applied to the whole selection.
      setFontSizeMixed(true)
      setFontSizeInputValue('')
      setFontSizeError(false)
    } else if (typeof style.pointSize === 'number') {
      setFontSizeMixed(false)
      setFontSize([style.pointSize])
      setFontSizeInputValue(String(style.pointSize))
      // A keyboard-driven Up/Down step over a selection that just got clamped at
      // FONT_SIZE_MIN/MAX flashes the same inline range error a typed out-of-range value
      // shows — read-and-cleared by `getEditingShapeTextSelectionStyle` itself, so this only
      // ever reflects the ONE step that just happened, never a stale prior attempt.
      setFontSizeError(style.pointSizeClampedAtBoundary === true)
    }
  }, [])

  /** Select-Tool whole-box selection: `size` is the first run's point size. */
  const showSelectionSize = useCallback((mixed: boolean, size: unknown) => {
    setFontSizeMixed(mixed)

    if (mixed) {
      setFontSizeInputValue('')
      setFontSizeError(false)
    } else if (typeof size === 'number') {
      setFontSize([size])
      setFontSizeInputValue(String(size))
      setFontSizeError(false)
    }
  }, [])

  /** Nothing selected: show the size a new frame would type at. */
  const resetToSize = useCallback((size: number) => {
    setFontSizeMixed(false)
    setFontSize([size])
    setFontSizeInputValue(String(size))
    setFontSizeError(false)
  }, [])

  return {
    fontSize,
    fontSizeInputValue,
    fontSizeError,
    fontSizeMixed,
    fontSizeControlsDisabled,
    fontSizeValue,
    appliedFontSize,
    activeFontSizePreset,
    handlePresetChange,
    handleSliderChange,
    handleInputChange,
    handleInputFocus,
    handleInputKeyDown,
    handleInputBlur,
    syncFromLiveEdit,
    showSelectionSize,
    resetToSize,
  }
}

export type FontSizeField = ReturnType<typeof useFontSizeField>
